import { Link } from 'react-router';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-base-100">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 lg:px-12 py-4 border-b border-base-300/30">
        <Link to="/">
          <div className="text-2xl font-extrabold tracking-tight">
            Hy<span className="text-primary">Pe</span>Ma
          </div>
        </Link>
        <Link to="/login" className="btn btn-ghost btn-sm text-base-content/60">
          Login
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-sm text-base-content/40 mb-10">Last updated: April 2026</p>

        <div className="space-y-10 text-base-content/70 text-sm leading-relaxed">

          {/* 1. Controller & Contact */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">1. Controller and Contact Information</h2>
            <p className="mb-3">
              The controller responsible for data processing on{' '}
              <a href="https://hypema.app" className="text-primary hover:underline">hypema.app</a>{' '}
              and the HyPeMa Chrome extension is:
            </p>
            <div className="bg-base-200/40 rounded-lg p-4 mb-3 space-y-1">
              <p className="font-medium text-base-content">OXO UG (haftungsbeschr&auml;nkt)</p>
              <p>R&uuml;benackerweg 2, 82418 Seehausen am Staffelsee, Germany</p>
              <p>Managing Director: Robert Stammen</p>
              <p>Commercial register: Amtsgericht M&uuml;nchen, HRB 223114</p>
              <p>VAT ID: DE304712598</p>
              <p>
                Email:{' '}
                <a href="mailto:service@newstelligent.com" className="text-primary hover:underline">
                  service@newstelligent.com
                </a>
              </p>
              <p>Phone: +49-89-5787-5350</p>
            </div>
            <p>
              As we employ fewer than 20 persons regularly engaged in automated data processing,
              we are not required to appoint a Data Protection Officer (&sect;38 BDSG). For any
              privacy-related inquiries, please contact us using the details above.
            </p>
          </section>

          {/* 2. Overview */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">2. Overview of the Service</h2>
            <p className="mb-3">
              HyPeMa is an AI-powered SaaS platform for hyper-personalized cold email outreach,
              designed exclusively for business-to-business (B2B) use. The service enables users to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-3">
              <li>Find and manage leads through AI-powered search, CSV import, or manual entry.</li>
              <li>Generate personalized email content per lead using artificial intelligence.</li>
              <li>Create Gmail drafts via the companion Chrome extension (&quot;HyPeMa &mdash; Gmail Draft Creator&quot;).</li>
            </ul>
            <p>
              Users send emails from their own Gmail accounts. HyPeMa does not send emails on
              behalf of users.
            </p>
          </section>

          {/* 3. Legal Bases */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">3. Legal Bases for Processing</h2>
            <p className="mb-3">We process personal data on the following legal bases under the GDPR:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Art. 6(1)(b) GDPR &mdash; Performance of a contract:</strong> Processing necessary
                to provide the HyPeMa service, including account creation, lead management, AI content
                generation, and billing.
              </li>
              <li>
                <strong>Art. 6(1)(a) GDPR &mdash; Consent:</strong> Where you have given consent, such as
                for analytics cookies (Google Analytics). You may withdraw consent at any time.
              </li>
              <li>
                <strong>Art. 6(1)(f) GDPR &mdash; Legitimate interest:</strong> For security measures,
                fraud prevention, and service improvement. Our legitimate interest is maintaining a
                secure, functional platform.
              </li>
              <li>
                <strong>Art. 6(1)(c) GDPR &mdash; Legal obligation:</strong> Retention of tax and
                commercial records as required by German law.
              </li>
            </ul>
          </section>

          {/* 4. Data We Collect */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">4. Data We Collect</h2>

            <h3 className="font-medium text-base-content mb-2">4.1 Account Data</h3>
            <p className="mb-4">
              When you sign up via Google OAuth, we receive and store your email address, display name,
              and Google user ID through Supabase Auth. This data is used for authentication and account
              management.
            </p>

            <h3 className="font-medium text-base-content mb-2">4.2 Company / Tenant Data</h3>
            <p className="mb-4">
              You may provide your company name, website URL, company description, company size,
              industry, and other details. We also store AI-researched company intelligence generated
              at your request.
            </p>

            <h3 className="font-medium text-base-content mb-2">4.3 Sender Profile</h3>
            <p className="mb-4">
              To personalize outreach, you provide your full name, job title, company affiliation,
              professional bio, and tone preferences.
            </p>

            <h3 className="font-medium text-base-content mb-2">4.4 Lead Data</h3>
            <p className="mb-4">
              For each lead, the following data may be stored: email address, first and last name,
              company name, job title, country, source URL, notes, and outreach status.
            </p>

            <h3 className="font-medium text-base-content mb-2">4.5 Generated Emails</h3>
            <p className="mb-4">
              AI-generated email content including subject lines, email bodies, and unsubscribe tokens
              is stored in your tenant&apos;s database.
            </p>

            <h3 className="font-medium text-base-content mb-2">4.6 Usage and Billing Data</h3>
            <p className="mb-4">
              We log API calls including the service used, operation type, tokens consumed, and the AI
              model used. This data is used for billing, rate limiting, and service monitoring.
            </p>

            <h3 className="font-medium text-base-content mb-2">4.7 Suppression List</h3>
            <p>
              When a recipient unsubscribes, we store a SHA-256 hash of their email address to ensure
              they are not contacted again. We do not store the plaintext email address in the
              suppression list.
            </p>
          </section>

          {/* 5. Chrome Extension */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">5. Chrome Extension</h2>

            <h3 className="font-medium text-base-content mb-2">What the extension does</h3>
            <p className="mb-4">
              The HyPeMa Chrome extension creates email drafts in your Gmail account. It receives
              draft content (recipient, subject, body) from the HyPeMa web application and automates
              the Gmail compose window to create drafts on your behalf.
            </p>

            <h3 className="font-medium text-base-content mb-2">Permissions and why we need them</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>storage</strong> &mdash; to save batch draft progress locally so it persists if
                the browser tab is refreshed.
              </li>
              <li>
                <strong>tabs</strong> &mdash; to open a Gmail tab when batch drafting starts and the user
                does not already have Gmail open.
              </li>
              <li>
                <strong>Host access to mail.google.com</strong> &mdash; to interact with the Gmail compose
                window and create drafts.
              </li>
              <li>
                <strong>Host access to hypema.app</strong> &mdash; to receive draft requests from the
                HyPeMa platform via the content script bridge.
              </li>
            </ul>

            <h3 className="font-medium text-base-content mb-2">Data stored locally by the extension</h3>
            <p className="mb-4">
              Only batch progress counters (e.g., &quot;draft 3 of 10 completed&quot;) are stored in{' '}
              <code className="text-xs bg-base-300/50 px-1 rounded">chrome.storage.local</code>.
              This data contains no personally identifiable information and is cleared after each batch
              completes.
            </p>

            <h3 className="font-medium text-base-content mb-2">What the extension does NOT do</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>It does <strong>not</strong> read your Gmail inbox, contacts, or existing emails.</li>
              <li>It does <strong>not</strong> send any email content to third-party services.</li>
              <li>It does <strong>not</strong> collect analytics, telemetry, or tracking data.</li>
              <li>It does <strong>not</strong> use cookies or tracking pixels.</li>
              <li>It does <strong>not</strong> sell, share, or monetize any user data.</li>
            </ul>
          </section>

          {/* 6. Cookies & Local Storage */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">6. Cookies and Local Storage</h2>

            <h3 className="font-medium text-base-content mb-2">Strictly necessary</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>auth_token</strong> (localStorage, JWT) &mdash; authenticates your session.
                Without this, the application cannot function.
              </li>
            </ul>

            <h3 className="font-medium text-base-content mb-2">Functional</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>theme</strong> (localStorage) &mdash; stores your preferred color theme.
              </li>
              <li>
                <strong>sidebar_collapsed</strong> (localStorage) &mdash; stores whether the sidebar
                is collapsed or expanded.
              </li>
            </ul>

            <h3 className="font-medium text-base-content mb-2">Analytics (consent required)</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Google Analytics cookies</strong> &mdash; used to understand how the service is
                used and to improve the user experience. These cookies are only set after you provide
                consent. You may withdraw consent at any time.
              </li>
            </ul>
          </section>

          {/* 7. Third-Party Services */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">7. Third-Party Services and Data Sharing</h2>
            <p className="mb-4">
              We share data with the following third-party service providers, acting as data processors
              on our behalf:
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-base-content mb-1">Supabase (EU &mdash; Frankfurt)</h3>
                <p>
                  Hosts our database and authentication infrastructure. All user data, tenant data, and
                  application data is stored in Supabase&apos;s EU-Frankfurt region. Supabase processes data
                  in accordance with their DPA and GDPR obligations.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-base-content mb-1">Anthropic (Claude API)</h3>
                <p className="mb-2">
                  HyPeMa uses Anthropic&apos;s Claude Opus 4 large language model via the Anthropic API to
                  power all AI features. The following data is sent to Anthropic for processing depending
                  on the operation:
                </p>
                <ul className="list-disc pl-6 space-y-1 mb-2">
                  <li>
                    <strong>Company research:</strong> Your company website URL. The AI model uses web
                    search to gather publicly available information about the company (name, product,
                    industry, leadership, value proposition).
                  </li>
                  <li>
                    <strong>Lead generation:</strong> Your search criteria (industry, role, region) and
                    your company context. The AI model searches the web for publicly available contact
                    information.
                  </li>
                  <li>
                    <strong>Email generation:</strong> Lead data (name, company, role, country), your
                    company profile, your pitch/goal description, and tone preferences. The AI model
                    generates a personalized email subject line and body.
                  </li>
                </ul>
                <p className="mb-2">
                  <strong>What Anthropic returns:</strong> Structured text output only (company profiles,
                  lead lists, email drafts). No personal data about you is included in AI responses.
                </p>
                <p>
                  Anthropic processes data in the United States under the EU-U.S. Data Privacy Framework.
                  Per Anthropic&apos;s API terms, data submitted via the API is <strong>not</strong> used
                  to train, improve, or fine-tune AI models. API inputs and outputs are retained by
                  Anthropic for up to 30 days for trust &amp; safety purposes, then deleted.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-base-content mb-1">Google</h3>
                <p>
                  Provides OAuth authentication for user sign-in and Google Analytics for anonymized
                  usage statistics (with your consent).
                </p>
              </div>

              <div>
                <h3 className="font-medium text-base-content mb-1">Cloudflare</h3>
                <p>
                  Provides CDN, DDoS protection, and Web Application Firewall (WAF) services.
                  Cloudflare processes request metadata (IP addresses, request headers) to protect
                  the service from malicious traffic.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-base-content mb-1">[Payment Provider]</h3>
                <p>
                  Processes subscription payments. Payment data (credit card details, billing address)
                  is handled directly by the payment provider and is not stored on our servers.
                </p>
              </div>
            </div>
          </section>

          {/* 8. International Data Transfers */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">8. International Data Transfers</h2>
            <p>
              Our primary database is hosted in the EU (Frankfurt) via Supabase. Some sub-processors
              (Anthropic, Google, Cloudflare) may process data in the United States. Where data is
              transferred outside the EEA, we ensure appropriate safeguards are in place, including
              EU Standard Contractual Clauses (SCCs) and, where applicable, the EU-U.S. Data Privacy
              Framework.
            </p>
          </section>

          {/* 9. Data Retention */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">9. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Account and application data:</strong> Retained for as long as your account is
                active. Upon account deletion, all associated data is permanently removed, subject to
                legal retention obligations.
              </li>
              <li>
                <strong>Server logs:</strong> Automatically deleted after 14 days.
              </li>
              <li>
                <strong>Tax and commercial records:</strong> Retained for 10 years in accordance with
                &sect;&sect;147 AO (Abgabenordnung) and 257 HGB (Handelsgesetzbuch), as required by
                German law.
              </li>
              <li>
                <strong>Suppression list (email hashes):</strong> Retained indefinitely to honor
                unsubscribe requests.
              </li>
            </ul>
          </section>

          {/* 10. Data Security */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">10. Data Security</h2>
            <p className="mb-3">
              We implement appropriate technical and organizational measures to protect your data:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Encryption in transit:</strong> All data is transmitted over TLS 1.3.
              </li>
              <li>
                <strong>Encryption at rest:</strong> Data is encrypted using AES-256.
              </li>
              <li>
                <strong>Tenant isolation:</strong> Row-level security (RLS) policies in Supabase ensure
                strict tenant data isolation.
              </li>
              <li>
                <strong>Web Application Firewall:</strong> Cloudflare WAF provides protection against
                common web attacks.
              </li>
              <li>
                <strong>Authentication:</strong> Handled by Supabase Auth with industry-standard
                encryption and Google OAuth integration.
              </li>
            </ul>
          </section>

          {/* 11. Automated Decision-Making */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">11. Automated Decision-Making &amp; Profiling</h2>
            <p className="mb-3">
              HyPeMa does not make decisions with legal effects or similarly significant impact solely
              through automated means (Art. 22 GDPR). All AI-generated outputs are suggestions that
              require human review before use.
            </p>
            <p>
              Specifically: AI-generated emails are presented as drafts for review. AI-generated lead
              lists are suggestions for the user to evaluate. Company research summaries are informational
              and may be inaccurate. The user always makes the final decision on whether to send any email
              or use any AI-generated content. You have the right to request an explanation of the logic
              involved in AI processing, to demand human review, and to express your point of view.
            </p>
          </section>

          {/* 11a. EU AI Act Transparency */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">12. AI Transparency (EU AI Act)</h2>
            <p className="mb-3">
              In accordance with Regulation (EU) 2024/1689 (the EU AI Act), we provide the following
              transparency information about our use of artificial intelligence:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>AI system used:</strong> Anthropic Claude Opus 4 (large language model),
                accessed via the Anthropic API. This is a general-purpose AI model classified under
                the EU AI Act as a general-purpose AI system.
              </li>
              <li>
                <strong>AI-generated content disclosure:</strong> All email content, company research
                summaries, and lead suggestions produced by HyPeMa are generated by artificial
                intelligence. These outputs may appear human-written but are machine-generated. Users
                are responsible for disclosing the use of AI-generated content to recipients where
                required by applicable law.
              </li>
              <li>
                <strong>Data used for AI processing:</strong> Only data you provide or that is publicly
                available on the web is used as input. Your data is not used to train, fine-tune, or
                improve any AI model.
              </li>
              <li>
                <strong>Limitations:</strong> AI outputs may contain factual errors, hallucinations,
                outdated information, or biased content. AI-generated emails and research should always
                be reviewed by a human before use or distribution.
              </li>
              <li>
                <strong>Human oversight:</strong> HyPeMa is designed with mandatory human-in-the-loop
                review. No AI-generated email is sent automatically &mdash; the user must manually
                review and send each email from their own Gmail account.
              </li>
            </ul>
          </section>

          {/* 12. B2B Data Processing */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">13. Responsibilities for Lead Data (B2B)</h2>
            <p className="mb-3">
              When you use HyPeMa to manage leads and send outreach, the following responsibilities apply:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>You (the user) are the data controller</strong> for the lead data you import,
                create, or generate through the platform.
              </li>
              <li>
                <strong>HyPeMa acts as a data processor</strong> on your behalf, processing lead data
                only according to your instructions and for the purpose of providing the service.
              </li>
              <li>
                You are responsible for ensuring that you have a lawful basis (e.g., legitimate interest
                under Art. 6(1)(f) GDPR) to contact the leads in your campaigns.
              </li>
              <li>
                You are responsible for honoring unsubscribe requests and complying with applicable
                anti-spam and data protection legislation in your jurisdiction and the jurisdiction of
                your leads.
              </li>
            </ul>
            <p className="mt-3">
              The Data Processing Agreement (DPA) is part of our{' '}
              <Link to="/terms#dpa" className="text-primary hover:underline">Terms of Service</Link>.
            </p>
          </section>

          {/* 13. Your Rights */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">14. Your Rights Under the GDPR</h2>
            <p className="mb-3">
              Under the General Data Protection Regulation, you have the following rights regarding
              your personal data:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Right of access (Art. 15)</strong> &mdash; You may request confirmation of whether
                we process your data and obtain a copy.
              </li>
              <li>
                <strong>Right to rectification (Art. 16)</strong> &mdash; You may request correction of
                inaccurate personal data.
              </li>
              <li>
                <strong>Right to erasure (Art. 17)</strong> &mdash; You may request deletion of your
                personal data, subject to legal retention obligations.
              </li>
              <li>
                <strong>Right to restriction of processing (Art. 18)</strong> &mdash; You may request
                restriction of processing in certain circumstances.
              </li>
              <li>
                <strong>Right to data portability (Art. 20)</strong> &mdash; You may request your data
                in a structured, commonly used, machine-readable format.
              </li>
              <li>
                <strong>Right to object (Art. 21)</strong> &mdash; You may object to processing based
                on legitimate interest at any time.
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:service@newstelligent.com" className="text-primary hover:underline">
                service@newstelligent.com
              </a>
              . We will respond within one month of receiving your request.
            </p>
          </section>

          {/* 14. Right to Lodge a Complaint */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">15. Right to Lodge a Complaint</h2>
            <p className="mb-3">
              If you believe that our processing of your personal data violates the GDPR, you have the
              right to lodge a complaint with a supervisory authority. Our competent supervisory
              authority is:
            </p>
            <div className="bg-base-200/40 rounded-lg p-4 space-y-1">
              <p className="font-medium text-base-content">
                Bayerisches Landesamt f&uuml;r Datenschutzaufsicht (BayLDA)
              </p>
              <p>Promenade 18, 91522 Ansbach, Germany</p>
              <p>
                Website:{' '}
                <a
                  href="https://www.lda.bayern.de"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.lda.bayern.de
                </a>
              </p>
            </div>
          </section>

          {/* 15. Changes */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">16. Changes to This Privacy Policy</h2>
            <p>
              We may update this privacy policy from time to time to reflect changes in our practices,
              technology, or legal requirements. We will notify registered users of material changes
              via email or an in-app notice. The &quot;Last updated&quot; date at the top of this page
              indicates when the policy was last revised.
            </p>
          </section>

          {/* 16. Contact */}
          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">17. Contact</h2>
            <p>
              For any questions about this privacy policy or our data practices, contact us at:{' '}
              <a href="mailto:service@newstelligent.com" className="text-primary hover:underline">
                service@newstelligent.com
              </a>
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-base-300/30 py-8 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-base-content/30">
          <span>Hy<span className="text-primary/60">Pe</span>Ma</span>
          <span>Precision over volume.</span>
        </div>
      </footer>
    </div>
  );
}
